"use client";

/**
 * P2-02 · Desktop primary nav + mega-menu interaction (P2-DB03 r2 FIXED
 * disclosure model — not role=menu, no roving tabindex):
 *
 * - Triggers are buttons with `aria-expanded`/`aria-controls`; each panel
 *   follows its trigger in DOM order so Tab walks the open panel's links
 *   with no focus scripting.
 * - ← → move focus across the top-level triggers/links (clamped at the
 *   ends — eng choice), Enter/Space toggles, Escape closes and returns
 *   focus to the trigger, focus leaving the nav closes the panel.
 * - Hover opens after a ~150ms intent delay (mouse pointers only; touch
 *   stays click-toggle) with a matching close grace; when a panel is
 *   already open, pointing at another trigger switches instantly.
 * - A full-width scrim dims the page behind an open panel (click closes).
 * - Overflow at just-above-breakpoint widths: the list scrolls
 *   horizontally rather than wrapping (eng choice per r2 note).
 */
import * as React from "react";

import { MegaMenuPanel } from "~/components/storefront/nav/mega-menu-panel";
import { useNav } from "~/components/storefront/nav/nav-provider";
import { LocaleLink, useTranslator } from "~/i18n/provider";
import { cn } from "~/lib/utils";

const HOVER_INTENT_MS = 150;

/** P17A-08 · Nav items come from `useNav()` — see `nav-provider.tsx`. */
export function PrimaryNav({ className }: { className?: string }) {
  const items = useNav();
  const t = useTranslator();
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const topLevelRefs = React.useRef<(HTMLElement | null)[]>([]);
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  React.useEffect(() => clearTimers, []);

  const openPanel = (index: number) => {
    clearTimers();
    setOpenIndex(index);
  };
  const closePanel = () => {
    clearTimers();
    setOpenIndex(null);
  };

  const onTriggerPointerEnter = (index: number, e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (openIndex !== null && openIndex !== index) {
      // A panel is already open — switching is instant per the spec.
      openPanel(index);
      return;
    }
    if (openIndex === index) return;
    clearTimers();
    openTimer.current = setTimeout(() => openPanel(index), HOVER_INTENT_MS);
  };
  const onZonePointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    clearTimers();
    if (openIndex !== null) {
      closeTimer.current = setTimeout(closePanel, HOVER_INTENT_MS);
    }
  };
  const onZonePointerEnter = (e: React.PointerEvent) => {
    // Re-entering the trigger/panel zone cancels a pending close.
    if (e.pointerType !== "mouse") return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const onNavKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (openIndex !== null) {
        const trigger = topLevelRefs.current[openIndex];
        closePanel();
        trigger?.focus();
        e.preventDefault();
      }
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const current = topLevelRefs.current.findIndex(
      (el) => el === document.activeElement,
    );
    if (current === -1) return; // arrows only re-map at the top level
    e.preventDefault();
    const next = e.key === "ArrowRight" ? current + 1 : current - 1;
    topLevelRefs.current[
      Math.max(0, Math.min(topLevelRefs.current.length - 1, next))
    ]?.focus();
  };

  const onNavBlur = (e: React.FocusEvent) => {
    // Close when focus leaves the whole nav (Tab past the last panel link).
    if (!e.currentTarget.contains(e.relatedTarget)) closePanel();
  };

  const onPanelItemClick = closePanel;

  return (
    <nav
      aria-label={t("nav.mainNavigation")}
      // Deliberately NOT positioned: panels anchor to the sticky <header>
      // (the nearest positioned ancestor) so they span the full width.
      className={className}
      onKeyDown={onNavKeyDown}
      onBlur={onNavBlur}
    >
      {openIndex !== null && (
        <div
          aria-hidden="true"
          data-testid="nav-scrim"
          // Dims the page *below* the header only (top-full = header bottom),
          // so the header/triggers keep full contrast and never sit under the
          // scrim — the two together were the mega-menu flicker + a contrast
          // regression. Anchors to the sticky header; below the panel (z-40).
          className="absolute inset-x-0 top-full z-30 h-screen bg-black/30"
          onClick={closePanel}
        />
      )}
      {/* h-full triggers meet the top-full panel with no hover dead-zone. The
          ul stays static so the absolute panel anchors to the full-width sticky
          header (not the ul); the scrim starts below the header, so triggers
          need no z-index to stay clear of it. */}
      <ul className="flex h-full items-stretch gap-5 overflow-x-auto text-sm font-medium [scrollbar-width:none]">
        {items.map((item, index) =>
          item.kind === "link" ? (
            <li
              key={`${item.label}:${item.href}`}
              className="flex flex-none items-center"
            >
              <LocaleLink
                ref={(el) => {
                  topLevelRefs.current[index] = el;
                }}
                href={item.href}
                onClick={() => onPanelItemClick()}
                className="text-fg hover:text-primary"
              >
                {item.label}
              </LocaleLink>
            </li>
          ) : (
            <li
              key={item.slug}
              className="flex flex-none items-stretch"
              onPointerEnter={onZonePointerEnter}
              onPointerLeave={onZonePointerLeave}
            >
              <button
                ref={(el) => {
                  topLevelRefs.current[index] = el;
                }}
                type="button"
                aria-expanded={openIndex === index}
                aria-controls={`mega-panel-${item.slug}`}
                onPointerEnter={(e) => onTriggerPointerEnter(index, e)}
                onClick={() =>
                  openIndex === index ? closePanel() : openPanel(index)
                }
                className={cn(
                  "flex h-full items-center gap-1 text-fg hover:text-primary",
                  openIndex === index && "text-primary",
                )}
              >
                {item.label}
                <span aria-hidden="true" className="text-[10px] text-fg-muted">
                  {openIndex === index ? "▴" : "▾"}
                </span>
              </button>
              {openIndex === index && (
                <div
                  onPointerEnter={onZonePointerEnter}
                  onPointerLeave={onZonePointerLeave}
                >
                  <MegaMenuPanel
                    id={`mega-panel-${item.slug}`}
                    item={item}
                    onItemClick={onPanelItemClick}
                  />
                </div>
              )}
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}
